/**
 * Pure cursor math for chart replay. No React, no Redux — imported by the
 * ohlc selector hook, the control bar and the Chart alike, so all three agree
 * on exactly which bars "up to the cursor" means.
 *
 * See features/chart_replay.md.
 */
import type { Candle } from "@/api/types";

/**
 * How many candles are at or before `cursorTime` — i.e. the length the
 * replayed series should be truncated to.
 *
 * Candles are sorted ascending by time (the loader sorts on merge), so this
 * is an upper-bound binary search: O(log n) per step instead of a scan, which
 * matters because it runs on every cursor tick over arrays of thousands.
 *
 * Returns 0 when the cursor sits before the first candle, and `candles.length`
 * when it is at or past the last one.
 */
export function replayEndIndex(candles: Candle[], cursorTime: number): number {
  let lo = 0;
  let hi = candles.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (candles[mid].time <= cursorTime) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/**
 * Move the cursor `delta` bars (negative = back), clamped to the loaded range.
 *
 * Stepping is defined over bar INDEXES, not by adding the timeframe to the
 * cursor time: adding seconds would land inside a weekend or a session gap
 * and stall, since no bar has that timestamp.
 *
 * `delta` may be more than one bar — the play clock advances several at a time
 * when the requested speed outruns the frame rate — so this clamps rather than
 * refusing: asking for 30 bars when 4 remain lands on the last one and reports
 * `atEnd`, instead of doing nothing and stalling playback short of the edge.
 *
 * `moved` distinguishes "clamped onto a new bar" from "already there", which
 * is what tells the caller to stop the clock.
 */
export interface CursorMove {
  time: number;
  atEnd: boolean;
  atStart: boolean;
  moved: boolean;
}

export function advanceCursor(
  candles: Candle[],
  cursorTime: number,
  delta: number,
): CursorMove | null {
  if (candles.length === 0) return null;
  // endIndex is a length; the cursor bar's own index is one less.
  const current = replayEndIndex(candles, cursorTime) - 1;
  const last = candles.length - 1;
  const next = Math.min(Math.max(current + delta, 0), last);
  return {
    time: candles[next].time,
    atEnd: next === last,
    atStart: next === 0,
    moved: next !== current,
  };
}

/** Clamp an arbitrary time (e.g. a click in the empty future area) back onto
 *  a real bar: the last bar at or before it, else the first bar. */
export function clampToBar(candles: Candle[], time: number): number | null {
  if (candles.length === 0) return null;
  const end = replayEndIndex(candles, time);
  return end === 0 ? candles[0].time : candles[end - 1].time;
}
